import { useCallback } from 'react';

import { Form, Field, FormController } from 'core/cells/forms';
import { FormTextField } from 'core/cells/form-text-field';
import { FlexBox } from 'core/cells/box';

import { useTranslations } from 'core/dna/translations';
import { WriteModeEnum } from 'core/dna/types/write-mode';

import { WriteTableModal } from 'core/organisms/write-modal';

import { Button } from 'templates/mdp/components/custom-buttons';
import { DeepWarning } from 'templates/mdp/components/typography/deep-warning';

import { useSchedulesTableWrite } from './use-schedules-table-write';

interface Values {
  name: string;
}

interface ScheduleFormProps {
  defaultName: string;
  id?: string;
  open?: boolean;
  setOpen?: (open: boolean) => void;
}

export const ScheduleEditor = (props: ScheduleFormProps) => {
  const { open, setOpen, id, defaultName } = props;

  const { onRowAdd, onRowUpdate } = useSchedulesTableWrite();
  const { lang, texts } = useTranslations();

  const onClose = useCallback(() => {
    setOpen?.(false);
  }, [setOpen]);

  return (
    <WriteTableModal
      isShow={open ?? false}
      onClose={onClose}
      writeMode={id ? WriteModeEnum.update : WriteModeEnum.create}
      title={
        id
          ? texts.planner.preparationSpace.schedulesModule.editSchedule
          : texts.planner.preparationSpace.schedulesModule.addSchedule
      }
    >
      {!id && (
        <DeepWarning>
          {texts.planner.preparationSpace.schedulesModule.addScheduleWarning}
        </DeepWarning>
      )}
      <FormController
        initialValues={{
          name: defaultName,
        }}
        validate={(values) => {
          const errors: Partial<Values> = {};
          if (!values.name) {
            errors.name = texts.oasCommon.validation.required;
          }
          return errors;
        }}
        onSubmit={async (values) => {
          setOpen?.(false);
          id
            ? await onRowUpdate({ id, name: values.name })
            : await onRowAdd({ name: values.name });
        }}
      >
        {({ submitForm, isSubmitting }) => (
          <Form>
            <Field
              component={FormTextField}
              name="name"
              type="text"
              label={lang.common.names.one}
              disabled={isSubmitting}
              fullWidth
            />
            <FlexBox justifyContent="flex-end">
              <Button
                color="secondary"
                disabled={isSubmitting}
                onClick={submitForm}
              >
                {id ? lang.common.save : lang.common.add}
              </Button>
              <Button onClick={onClose} color="secondary" simple>
                {lang.common.close}
              </Button>
            </FlexBox>
          </Form>
        )}
      </FormController>
    </WriteTableModal>
  );
};
